/**
 * AdminTransactionHistory Screen
 * Global transaction history screen with filtering and search capabilities.
 * Displays all system transactions (payments, wallet credits/debits, transfers).
 */

import { useIsFocused } from "@react-navigation/native";
import { useContext, useEffect, useState, useRef } from "react";
import {
  FlatList,
  Modal,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import PageTitle from "../../components/PageTitle";
import TextBoxIcon from "../../components/TextBoxIcon";
import TransactionDetailModal from "../../components/TransactionDetailModal";
import { GlobalContext } from "../../contexts/global";
import { getTransactionHistory, getTransactionDetail } from "../../services/Api/admin";
import {
  TRANSACTION_TYPES,
  TRANSACTION_TYPE_LABELS,
  formatTransactionAmount,
  formatTransactionDate,
  getTransactionTypeIcon,
  getTransactionAmountColor,
  getTransactionStatusColor,
  getTransactionStatusLabel,
} from "../../helpers/transactionHelpers";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";

const PAGE_SIZE = 20;

// Transaction type filter options
const TRANSACTION_TYPE_OPTIONS = [
  { label: "Todos", value: null },
  { label: "Pagamento", value: TRANSACTION_TYPES.PAYMENT },
  { label: "Crédito Carteira", value: TRANSACTION_TYPES.WALLET_CREDIT },
  { label: "Débito Carteira", value: TRANSACTION_TYPES.WALLET_DEBIT },
  { label: "Transferência", value: TRANSACTION_TYPES.TRANSFER },
];

// Transaction status filter options
const TRANSACTION_STATUS_OPTIONS = [
  { label: "Todos", value: null },
  { label: "Sucesso", value: "approved" },
  { label: "Não finalizado", value: "failed" },
];

export default function AdminTransactionHistory({ navigation }) {
  const { setLoading, setShowAlert } = useContext(GlobalContext);
  const isFocused = useIsFocused();

  // Transaction list state
  const [transactions, setTransactions] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Filter state
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [type, setType] = useState(null);
  const [status, setStatus] = useState(null);
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showStatusPicker, setShowStatusPicker] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [specificDate, setSpecificDate] = useState("");

  // Detail modal state
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Refs to prevent duplicate calls
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const searchTimeoutRef = useRef(null);

  // Load transactions from API
  const loadTransactions = async (pageNum = 1, reset = false) => {
    // Prevent concurrent calls
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;

    if (!reset) {
      setLoadingMore(true);
    }

    const filters = {
      fromDate,
      toDate,
      type,
      status,
      search: search || null,
    };

    try {
      const response = await getTransactionHistory(filters, pageNum, PAGE_SIZE);
      const newTransactions = response.items || [];
      
      if (reset) {
        setTransactions(newTransactions);
      } else {
        setTransactions((prev) => [...prev, ...newTransactions]);
      }
      setPage(pageNum);
      setHasMore(pageNum < response.totalPages);
    } catch (error) {
      const message = error?.message || error?.erro || "Erro ao carregar transações";
      setShowAlert({
        type: "alert",
        title: "Erro",
        message: message,
        show: true,
        confirmText: "OK",
        pressConfirm: () => setShowAlert((prev) => ({ ...prev, show: false })),
      });
    } finally {
      setLoading({ show: false, message: "" });
      setRefreshing(false);
      setLoadingMore(false);
      isLoadingRef.current = false;
      hasLoadedRef.current = true;
    }
  };

  // Initial load when screen is focused
  useEffect(() => {
    if (isFocused && !hasLoadedRef.current) {
      setLoading({ show: true, message: "Carregando transações" });
      loadTransactions(1, true);
    }
  }, [isFocused]);

  // Reload when filters change (except search)
  useEffect(() => {
    if (hasLoadedRef.current) {
      loadTransactions(1, true);
    }
  }, [fromDate, toDate, type, status]);

  // Debounce search
  useEffect(() => {
    if (!hasLoadedRef.current) return;
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      loadTransactions(1, true);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Handle pull-to-refresh
  const handleRefresh = () => {
    setRefreshing(true);
    loadTransactions(1, true);
  };

  // Handle pagination (load more)
  const handleLoadMore = () => {
    if (!hasMore || isLoadingRef.current || loadingMore) return;
    loadTransactions(page + 1, false);
  };

  // Handle transaction row press - load detail and show modal
  const handleTransactionPress = async (transaction) => {
    setShowDetailModal(true);

    try {
      const detail = await getTransactionDetail(transaction.transactionId);
      setSelectedTransaction(detail);
    } catch (error) {
      setShowDetailModal(false);
      const message = error?.message || error?.erro || "Erro ao carregar detalhes da transação";
      setShowAlert({
        type: "alert",
        title: "Erro",
        message: message,
        show: true,
        confirmText: "OK",
        pressConfirm: () => setShowAlert((prev) => ({ ...prev, show: false })),
      });
    }
  };

  // Close detail modal
  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedTransaction(null);
  };

  // Set date filter with quick select buttons
  const setDateFilter = (daysAgo) => {
    setSpecificDate("");
    if (daysAgo === null) {
      setFromDate(null);
      setToDate(null);
      return;
    }
    const now = new Date();
    const from = new Date();
    from.setDate(now.getDate() - daysAgo);
    from.setHours(0, 0, 0, 0);
    now.setHours(23, 59, 59, 999);
    setFromDate(from);
    setToDate(now);
  };

  // Set specific date filter (DD/MM/YYYY format)
  const applySpecificDate = () => {
    if (!specificDate || specificDate.length !== 10) return;
    
    const parts = specificDate.split("/");
    if (parts.length !== 3) return;
    
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    
    if (isNaN(day) || isNaN(month) || isNaN(year)) return;
    
    const from = new Date(year, month, day, 0, 0, 0, 0);
    const to = new Date(year, month, day, 23, 59, 59, 999);
    
    if (isNaN(from.getTime())) return;
    
    setFromDate(from);
    setToDate(to);
    setShowDatePicker(false);
  };

  // Clear all filters
  const clearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setType(null);
    setStatus(null);
    setSearch("");
    setSpecificDate("");
  };

  // Check if any filter is active
  const hasActiveFilters = fromDate || toDate || type || status || search;

  // Get label for selected type
  const getSelectedTypeLabel = () => {
    const found = TRANSACTION_TYPE_OPTIONS.find((opt) => opt.value === type);
    return found ? found.label : "Todos";
  };

  // Get label for selected status
  const getSelectedStatusLabel = () => {
    const found = TRANSACTION_STATUS_OPTIONS.find((opt) => opt.value === status);
    return found ? found.label : "Todos";
  };

  // Get current date filter label
  const getDateFilterLabel = () => {
    if (!fromDate && !toDate) return "Todos";
    if (specificDate) return specificDate;
    
    const now = new Date();
    const diffDays = Math.round((now - fromDate) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Hoje";
    if (diffDays === 7) return "7 dias";
    if (diffDays === 30) return "30 dias";
    
    return `${fromDate.toLocaleDateString("pt-BR")}`;
  };

  // Render transaction list item
  const renderTransactionItem = ({ item }) => {
    const amountColor = getTransactionAmountColor(item.transactionType, item.amount);
    const statusColor = getTransactionStatusColor(item.status, item.statusMessage, item.transactionType);
    const statusLabel = getTransactionStatusLabel(item.status, item.statusMessage, item.transactionType);

    return (
      <TouchableOpacity
        style={styles.transactionItem}
        onPress={() => handleTransactionPress(item)}
        activeOpacity={0.7}
      >
        <View style={styles.transactionIconContainer}>
          <Icon
            name={getTransactionTypeIcon(item.transactionType)}
            size={24}
            color={amountColor}
          />
        </View>
        <View style={styles.transactionInfo}>
          <View style={styles.transactionHeader}>
            <Text style={styles.transactionType} numberOfLines={1}>
              {TRANSACTION_TYPE_LABELS[item.transactionType] || item.transactionType}
            </Text>
            <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
              <Text style={styles.statusText}>{statusLabel}</Text>
            </View>
          </View>
          <Text style={styles.transactionUser} numberOfLines={1}>
            {item.userName || item.userEmail || "Usuário desconhecido"}
          </Text>
          <View style={styles.transactionFooter}>
            <Text style={styles.transactionDate}>
              {formatTransactionDate(item.dateTime)}
            </Text>
            <Text style={[styles.transactionAmount, { color: amountColor }]}>
              {formatTransactionAmount(item.amount)}
            </Text>
          </View>
        </View>
        <Icon name="chevron-right" size={24} color={Colors.GRAY_DARK} />
      </TouchableOpacity>
    );
  };

  // Render empty list state
  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <Icon name="receipt" size={128} color={Colors.BLACK} />
      <Text style={styles.emptyText}>
        {hasActiveFilters
          ? "Nenhuma transação encontrada para os filtros selecionados."
          : "Nenhuma transação registrada."}
      </Text>
    </View>
  );

  // Render footer with load more button
  const renderFooter = () => {
    if (loadingMore) {
      return (
        <View style={styles.footerLoader}>
          <Text style={styles.loadingText}>Carregando mais...</Text>
        </View>
      );
    }
    
    if (hasMore && transactions.length > 0) {
      return (
        <TouchableOpacity style={styles.loadMoreButton} onPress={handleLoadMore}>
          <Text style={styles.loadMoreText}>Carregar mais</Text>
        </TouchableOpacity>
      );
    }
    
    return null;
  };

  // Render filters section
  const renderFiltersSection = () => (
    <View style={styles.filtersContainer}>
      <TouchableOpacity
        style={styles.filterToggle}
        onPress={() => setShowFilters(!showFilters)}
      >
        <Icon
          name={showFilters ? "filter-off" : "filter"}
          size={20}
          color={Colors.PRIMARY}
        />
        <Text style={styles.filterToggleText}>
          {showFilters ? "Ocultar Filtros" : "Mostrar Filtros"}
        </Text>
        {hasActiveFilters && (
          <View style={styles.filterBadge}>
            <Text style={styles.filterBadgeText}>!</Text>
          </View>
        )}
      </TouchableOpacity>

      {showFilters && (
        <View style={styles.filtersContent}>
          {/* Search Input */}
          <Text style={styles.filterLabel}>Buscar</Text>
          <TextBoxIcon
            iconName="magnify"
            placeholderInput="Ticket, placa, email ou nome"
            value={search}
            onChangeTextInput={setSearch}
            styleBox={styles.searchBox}
          />

          {/* Transaction Type Filter */}
          <Text style={styles.filterLabel}>Tipo</Text>
          <TouchableOpacity
            style={styles.filterSelect}
            onPress={() => setShowTypePicker(true)}
          >
            <Text style={styles.filterSelectText}>{getSelectedTypeLabel()}</Text>
            <Icon name="chevron-down" size={20} color={Colors.GRAY_DARK} />
          </TouchableOpacity>

          {/* Transaction Status Filter */}
          <Text style={styles.filterLabel}>Status</Text>
          <TouchableOpacity
            style={styles.filterSelect}
            onPress={() => setShowStatusPicker(true)}
          >
            <Text style={styles.filterSelectText}>{getSelectedStatusLabel()}</Text>
            <Icon name="chevron-down" size={20} color={Colors.GRAY_DARK} />
          </TouchableOpacity>

          {/* Date Range Filter */}
          <Text style={styles.filterLabel}>Período</Text>
          <View style={styles.dateRangeButtons}>
            <TouchableOpacity
              style={[
                styles.dateRangeButton,
                !fromDate && !toDate && styles.dateRangeButtonActive,
              ]}
              onPress={() => setDateFilter(null)}
            >
              <Text
                style={[
                  styles.dateRangeButtonText,
                  !fromDate && !toDate && styles.dateRangeButtonTextActive,
                ]}
              >
                Todos
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setDateFilter(0)}
            >
              <Text style={styles.dateRangeButtonText}>Hoje</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setDateFilter(7)}
            >
              <Text style={styles.dateRangeButtonText}>7 dias</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setDateFilter(30)}
            >
              <Text style={styles.dateRangeButtonText}>30 dias</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.dateRangeButton}
              onPress={() => setShowDatePicker(true)}
            >
              <Icon name="calendar" size={16} color={Colors.PRIMARY} />
              <Text style={[styles.dateRangeButtonText, { marginLeft: 4 }]}>
                Data específica
              </Text>
            </TouchableOpacity>
          </View>

          {/* Current date filter display */}
          {(fromDate || toDate) && (
            <View style={styles.currentFilterDisplay}>
              <Icon name="calendar-check" size={16} color={Colors.PRIMARY} />
              <Text style={styles.currentFilterText}>
                Filtro: {getDateFilterLabel()}
              </Text>
            </View>
          )}

          {/* Clear Filters */}
          {hasActiveFilters && (
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={clearFilters}
            >
              <Icon name="close-circle" size={16} color={Colors.ALERT} />
              <Text style={styles.clearFiltersText}>Limpar Filtros</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );

  // Render transaction type picker modal
  const renderTypePicker = () => (
    <Modal
      visible={showTypePicker}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowTypePicker(false)}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowTypePicker(false)}
      >
        <View style={styles.pickerModal}>
          <Text style={styles.pickerTitle}>Selecionar Tipo de Transação</Text>
          {TRANSACTION_TYPE_OPTIONS.map((option) => (
            <TouchableOpacity
              key={option.value || "all"}
              style={[
                styles.pickerOption,
                type === option.value && styles.pickerOptionSelected,
              ]}
              onPress={() => {
                setType(option.value);
                setShowTypePicker(false);
              }}
            >
              <Text
                style={[
                  styles.pickerOptionText,
                  type === option.value && styles.pickerOptionTextSelected,
                ]}
              >
                {option.label}
              </Text>
              {type === option.value && (
                <Icon name="check" size={20} color={Colors.PRIMARY} />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // Render status picker modal
  const renderStatusPicker = () => (
    <Modal
      visible={showStatusPicker}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowStatusPicker(false)}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowStatusPicker(false)}
      >
        <View style={styles.pickerModal}>
          <Text style={styles.pickerTitle}>Selecionar Status</Text>
          {TRANSACTION_STATUS_OPTIONS.map((option) => (
            <TouchableOpacity
              key={option.value || "all"}
              style={[
                styles.pickerOption,
                status === option.value && styles.pickerOptionSelected,
              ]}
              onPress={() => {
                setStatus(option.value);
                setShowStatusPicker(false);
              }}
            >
              <Text
                style={[
                  styles.pickerOptionText,
                  status === option.value && styles.pickerOptionTextSelected,
                ]}
              >
                {option.label}
              </Text>
              {status === option.value && (
                <Icon name="check" size={20} color={Colors.PRIMARY} />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // Render date picker modal
  const renderDatePicker = () => (
    <Modal
      visible={showDatePicker}
      transparent={true}
      animationType="fade"
      onRequestClose={() => setShowDatePicker(false)}
    >
      <TouchableOpacity
        style={styles.modalOverlay}
        activeOpacity={1}
        onPress={() => setShowDatePicker(false)}
      >
        <View style={styles.pickerModal}>
          <Text style={styles.pickerTitle}>Selecionar Data</Text>
          <Text style={styles.dateInputLabel}>Digite a data (DD/MM/AAAA):</Text>
          <TextBoxIcon
            iconName="calendar"
            placeholderInput="DD/MM/AAAA"
            value={specificDate}
            onChangeTextInput={(text) => {
              // Auto-format as DD/MM/YYYY
              let formatted = text.replace(/\D/g, "");
              if (formatted.length > 2) {
                formatted = formatted.slice(0, 2) + "/" + formatted.slice(2);
              }
              if (formatted.length > 5) {
                formatted = formatted.slice(0, 5) + "/" + formatted.slice(5, 9);
              }
              //setSpecificDate(formatted);
            }}
            keyboardType="numeric"
            maxLength={10}
            styleBox={styles.dateInputBox}
          />
          <View style={styles.datePickerButtons}>
            <TouchableOpacity
              style={[styles.datePickerButton, styles.datePickerButtonCancel]}
              onPress={() => {
                setSpecificDate("");
                setShowDatePicker(false);
              }}
            >
              <Text style={styles.datePickerButtonCancelText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.datePickerButton, styles.datePickerButtonConfirm]}
              onPress={applySpecificDate}
            >
              <Text style={styles.datePickerButtonConfirmText}>Aplicar</Text>
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    </Modal>
  );

  // Separator component
  const ItemSeparator = () => <View style={styles.separator} />;

  return (
    <PageTitle title={"Histórico de Transações"} navigation={navigation}>
      <View style={styles.container}>
        {renderFiltersSection()}
        <FlatList
          data={transactions}
          renderItem={renderTransactionItem}
          keyExtractor={(item, index) => `${item.transactionId}_${index}`}
          contentContainerStyle={transactions.length === 0 ? styles.emptyList : null}
          ListEmptyComponent={renderEmptyList}
          ListFooterComponent={renderFooter}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[Colors.PRIMARY]}
              tintColor={Colors.PRIMARY}
            />
          }
          ItemSeparatorComponent={ItemSeparator}
        />
        {renderTypePicker()}
        {renderStatusPicker()}
        {renderDatePicker()}
        <TransactionDetailModal
          visible={showDetailModal}
          transaction={selectedTransaction}
          onClose={handleCloseDetailModal}
        />
      </View>
    </PageTitle>
  );
}


const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.WHITE,
  },
  // Filters section
  filtersContainer: {
    backgroundColor: Colors.SNOW,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  filterToggle: {
    flexDirection: "row",
    alignItems: "center",
    padding: 12,
  },
  filterToggleText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    marginLeft: 8,
    fontWeight: "bold",
  },
  filterBadge: {
    backgroundColor: Colors.ALERT,
    borderRadius: 10,
    width: 20,
    height: 20,
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
  },
  filterBadgeText: {
    color: Colors.WHITE,
    fontSize: Fonts.FONT_SIZE_12,
    fontWeight: "bold",
  },
  filtersContent: {
    padding: 12,
    paddingTop: 0,
  },
  filterLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 8,
    marginTop: 8,
  },
  searchBox: {
    marginBottom: 0,
  },
  filterSelect: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: Colors.WHITE,
    borderWidth: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderRadius: 8,
    padding: 12,
  },
  filterSelectText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
  },
  dateRangeButtons: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  dateRangeButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.WHITE,
    borderWidth: 1,
    borderColor: Colors.GRAY_LIGHT,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  dateRangeButtonActive: {
    backgroundColor: Colors.PRIMARY,
    borderColor: Colors.PRIMARY,
  },
  dateRangeButtonText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.BLACK,
  },
  dateRangeButtonTextActive: {
    color: Colors.WHITE,
  },
  currentFilterDisplay: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: Colors.WHITE,
    borderWidth: 1,
    borderColor: Colors.PRIMARY,
    borderRadius: 8,
    padding: 8,
    marginTop: 4,
  },
  currentFilterText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.PRIMARY,
    marginLeft: 8,
  },
  clearFiltersButton: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
  },
  clearFiltersText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.ALERT,
    marginLeft: 4,
  },

  // Transaction list item
  transactionItem: {
    flexDirection: "row",
    alignItems: "center",
    padding: 16,
    backgroundColor: Colors.WHITE,
  },
  transactionIconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Colors.SNOW,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  transactionInfo: {
    flex: 1,
  },
  transactionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
  },
  transactionType: {
    fontSize: Fonts.FONT_SIZE_14,
    fontWeight: "bold",
    color: Colors.BLACK,
    flex: 1,
    marginRight: 8,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 12,
  },
  statusText: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  transactionUser: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.PRIMARY,
    marginBottom: 4,
  },
  transactionFooter: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  transactionDate: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
  },
  transactionAmount: {
    fontSize: Fonts.FONT_SIZE_14,
    fontWeight: "bold",
  },
  separator: {
    height: 1,
    backgroundColor: Colors.GRAY_LIGHT,
    marginHorizontal: 16,
  },

  // Empty state
  emptyContainer: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    paddingVertical: 48,
  },
  emptyText: {
    marginTop: 24,
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
    textAlign: "center",
    paddingHorizontal: 32,
  },
  emptyList: {
    flexGrow: 1,
  },

  // Footer loader
  footerLoader: {
    padding: 16,
    alignItems: "center",
  },
  loadingText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
  },
  loadMoreButton: {
    padding: 16,
    alignItems: "center",
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: Colors.PRIMARY,
  },
  loadMoreText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },

  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
  },
  pickerModal: {
    backgroundColor: Colors.WHITE,
    borderRadius: 12,
    padding: 16,
    width: "80%",
    maxHeight: "60%",
  },
  pickerTitle: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 16,
    textAlign: "center",
  },
  pickerOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  pickerOptionSelected: {
    backgroundColor: Colors.SNOW,
  },
  pickerOptionText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
  },
  pickerOptionTextSelected: {
    color: Colors.PRIMARY,
    fontWeight: "bold",
  },

  // Date picker styles
  dateInputLabel: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.GRAY_DARK,
    marginBottom: 8,
  },
  dateInputBox: {
    marginBottom: 0,
  },
  datePickerButtons: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 16,
  },
  datePickerButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: "center",
  },
  datePickerButtonCancel: {
    backgroundColor: Colors.GRAY_LIGHT,
    marginRight: 8,
  },
  datePickerButtonConfirm: {
    backgroundColor: Colors.PRIMARY,
    marginLeft: 8,
  },
  datePickerButtonCancelText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.BLACK,
    fontWeight: "bold",
  },
  datePickerButtonConfirmText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
});
